import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSelector } from 'react-redux';
import { DataTable } from '../components/DataTable';
import { ExternalRecipient } from '../features/externalRecipients/types';
import {
  loadExternalRecipients,
  addExternalRecipient,
  editExternalRecipient,
  removeExternalRecipient,
  setExternalRecipientsSearch,
} from '../features/externalRecipients/externalRecipientsSlice';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ExternalRecipientForm,
  type ExternalRecipientFormValues,
} from '../components/ExternalRecipientForm';

const columnHelper = createColumnHelper<ExternalRecipient>();

const ROLE_OPTIONS = [
  { value: 'empresa', label: 'Empresa' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'otro', label: 'Otro' },
];

const getRoleLabel = (role: string) => {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label || role;
};

function ExternalRecipientActionsCell({
  recipient,
  onEdit,
  onDelete,
}: {
  recipient: ExternalRecipient;
  onEdit: (item: ExternalRecipient) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        onClick={() => onEdit(recipient)}
      >
        Editar
      </button>
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        onClick={() => onDelete(recipient.id)}
      >
        Eliminar
      </button>
    </div>
  );
}

export function ExternalRecipientsPage() {
  const { confirm } = useConfirmDialog();
  const dispatch = useAppDispatch();
  const { items, loading, error, pagination, search } = useSelector(
    (state: RootState) => state.externalRecipients,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExternalRecipient | null>(null);
  const [formError, setFormError] = useState('');
  const [userActionError, setUserActionError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      loadExternalRecipients({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search,
        includeInactive: true,
      }),
    );
  }, [dispatch, pagination.page, pagination.pageSize, search]);

  const reload = useCallback(() => {
    dispatch(
      loadExternalRecipients({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search,
        includeInactive: true,
      }),
    );
  }, [dispatch, pagination.page, pagination.pageSize, search]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setFormError('');
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((item: ExternalRecipient) => {
    setEditing(item);
    setFormError('');
    setDialogOpen(true);
  }, []);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditing(null);
      setFormError('');
    }
  }, []);

  const handleSubmitForm = async (values: ExternalRecipientFormValues) => {
    setFormError('');
    setSubmitting(true);
    try {
      if (editing) {
        const payload: {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          role?: string;
          active?: boolean;
        } = {};
        if (values.firstName !== undefined) payload.firstName = values.firstName;
        if (values.lastName !== undefined) payload.lastName = values.lastName;
        if (values.email !== undefined) payload.email = values.email;
        if (values.phone !== undefined) payload.phone = values.phone || undefined;
        if (values.role !== undefined) payload.role = values.role;
        if (values.active !== undefined) payload.active = values.active;

        const result = await dispatch(editExternalRecipient({ id: editing.id, payload }));
        if (editExternalRecipient.fulfilled.match(result)) {
          setDialogOpen(false);
          reload();
        } else {
          setFormError(result.error?.message || 'Error al actualizar el destinatario');
        }
      } else {
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone || undefined,
          role: values.role,
        };

        const result = await dispatch(addExternalRecipient(payload));
        if (addExternalRecipient.fulfilled.match(result)) {
          setDialogOpen(false);
          reload();
        } else {
          setFormError(result.error?.message || 'Error al crear el destinatario');
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = useCallback(
    async (id: string) => {
      setUserActionError('');
      const ok = await confirm({
        title: 'Eliminar destinatario',
        message: '¿Está seguro de que desea eliminar este destinatario?',
        confirmText: 'Eliminar',
        variant: 'danger',
      });
      if (!ok) return;
      const result = await dispatch(removeExternalRecipient(id));
      if (removeExternalRecipient.rejected.match(result) && result.payload) {
        setUserActionError(result.payload);
      } else {
        reload();
      }
    },
    [confirm, dispatch, reload],
  );

  const actionsCellRenderer = useCallback(
    (info: { row: { original: ExternalRecipient } }) => (
      <ExternalRecipientActionsCell
        recipient={info.row.original}
        onEdit={openEdit}
        onDelete={handleDelete}
      />
    ),
    [openEdit, handleDelete],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: 'name',
        header: 'Nombre',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('role', {
        header: 'Rol',
        cell: (info) => getRoleLabel(info.getValue()),
      }),
      columnHelper.accessor('phone', {
        header: 'Teléfono',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('active', {
        header: 'Estado',
        cell: (info) => {
          const active = info.getValue();
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                active
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
              }`}
            >
              {active ? 'Activo' : 'Inactivo'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Acciones',
        cell: actionsCellRenderer,
      }),
    ],
    [actionsCellRenderer],
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Destinatarios Externos</h1>
        <button
          className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={openCreate}
        >
          + Nuevo destinatario
        </button>
      </div>

      <div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-foreground mb-1">
            Buscar destinatarios
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => dispatch(setExternalRecipientsSearch(e.target.value))}
          />
        </div>
      </div>

      <div>
        {(error || userActionError) && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
            {userActionError || error}
          </div>
        )}
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No hay destinatarios registrados"
          pageSizeOptions={[10, 20, 50]}
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar destinatario' : 'Nuevo destinatario'}
            </DialogTitle>
          </DialogHeader>
          <ExternalRecipientForm
            initial={editing}
            loading={submitting}
            error={formError}
            onSubmit={handleSubmitForm}
            onCancel={() => handleDialogOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
